import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import {
  WSActions,
  WSCard,
  WSField,
  WSInputText,
  WSList,
  WSStripeInput,
  WSStripeInputRef
} from "@wingspanhq/fe-component-library";
import {
  SubscriptionPackage,
  SubscriptionPackageTier,
  SubscriptionTerm
} from "@wingspanhq/users/dist/lib/interfaces";
import { useWSMutation } from "@ws-react-query";
import { useMemo, useRef, useState } from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { OnboardingStep } from "../../../Onboarding/types";
import { useUserId } from "../../../../query/hooks/helpers";
import { useMutationCreateOrUpdateSubscriptionV3 } from "../../../../query/subscriptions/mutations";
import { useQuerySubscriptionPackages } from "../../../../query/subscriptions/queries";
import { PremiumPerksPlan } from "./PremiumPerksPlan";
import { updateBenefitsService } from "../../../../services/benefits";

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`);

export const StepSubscription: React.FC<OnboardingStep> = props => {
  const [annualBilling, setAnnualBilling] = useState(true);

  const [stripeInputState, setStripeInputState] = useState<
    StripeCardElementChangeEvent | undefined
  >();

  const stripeInput = useRef<WSStripeInputRef>(null);

  const [promoCode, setPromoCode] = useState("");

  const querySubscriptionPackages = useQuerySubscriptionPackages();

  const subscriptionPackage = useMemo(
    () =>
      annualBilling
        ? querySubscriptionPackages.data?.Benefits.Basic.Yearly
        : querySubscriptionPackages.data?.Benefits.Basic.Monthly,
    [annualBilling, querySubscriptionPackages.data]
  );

  const userId = useUserId();
  const [updateSubscription, updateSubscriptionMeta] =
    useMutationCreateOrUpdateSubscriptionV3(userId);

  const [mutate, meta] = useWSMutation(
    async () => {
      // Update payment method
      updateSubscriptionMeta.reset();

      if (!subscriptionPackage) {
        throw new Error("Failed to load subscription package");
      }

      if (!stripeInputState?.complete) {
        throw new Error("Please enter payment method details");
      }

      const token = (await stripeInput?.current?.createPaymentMethod())?.token
        ?.id;

      if (!token) {
        throw new Error("Failed to create payment method");
      }

      await updateSubscription(
        {
          package: SubscriptionPackage.Benefits,
          packageTier: SubscriptionPackageTier.Basic,
          term: annualBilling
            ? SubscriptionTerm.Yearly
            : SubscriptionTerm.Monthly,
          paymentMethodId: token,
          discountCode: promoCode || undefined
        },
        {
          throwOnError: true
        }
      );

      await updateBenefitsService(userId, {
        enabled: true
      });
    },
    {
      onSuccess: props.onNext
    }
  );

  return (
    <WSList gap="2XL">
      <PremiumPerksPlan
        value={annualBilling}
        onChange={setAnnualBilling}
        package={subscriptionPackage}
      />
      <WSCard>
        <WSList gap="L">
          <WSField label="Payment Information (Secured by Stripe)" required>
            <WSStripeInput
              ref={stripeInput}
              stripeInstance={stripePromise}
              onInputChange={event => {
                setStripeInputState(event);
              }}
              name="start"
            />
          </WSField>
          <WSInputText
            disabled={!stripeInputState?.complete}
            label="Promo code"
            name="promo"
            value={promoCode}
            onChange={setPromoCode}
          />
        </WSList>
      </WSCard>

      <WSErrorMessage
        contextKey="Subscription"
        forceShowApiErrors
        error={meta.error}
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Tertiary",
            onClick: props.onBack,
            label: "Back"
          },
          {
            label: "Confirm and enroll",
            disabled: !stripeInputState?.complete,
            onClick: () => {
              mutate();
            },
            loading: meta.isLoading
          }
        ]}
      />
    </WSList>
  );
};
